import { useEffect, useRef, useState } from 'react';
import { MOCK, isSchemaPending } from '../lib/supabase.js';
import * as db from '../lib/data.js';
import NameTh from '../components/NameTh.jsx';
import DbPending from '../components/DbPending.jsx';
import { verdictOf, permitRows, staleRule, suggestHs, suggestFromProducts } from '../lib/screening.js';

// TASK 6 — Pre-Import Screening: สินค้าชิ้นนี้นำเข้าได้ไหม / อากรเท่าไหร่ / ต้องขออะไร
// ก่อนจะสั่งของ — คัดกรองเบื้องต้น (ยึด broker/Advance Ruling เป็นคำวินิจฉัยจริง)

export default function Screening({ focusHs, onPick, onMissing }) {
  const [rules, setRules] = useState([]);
  const [term, setTerm] = useState('');
  const [picked, setPicked] = useState(null);      // hs_rule row
  const [products, setProducts] = useState([]);    // classification ที่ match พิกัดนี้
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestText, setSuggestText] = useState('');
  const [suggestions, setSuggestions] = useState([]); // [{line, prods, cands}]
  const [classes, setClasses] = useState([]);         // คลังสินค้าที่เคยนำเข้า
  const [freq, setFreq] = useState([]);               // พิกัดที่ใช้บ่อย

  const fail = (e) => { if (isSchemaPending(e)) setPending(true); else setError(e.message ?? String(e)); };
  // รีเฟรชตอนเปิดพิกัดไว้ → เปิดพิกัดเดิมให้ แต่ถ้าคนกดพิกัดอื่นไปก่อนที่ของเดิมโหลดเสร็จ = ของคนกดชนะ
  const restoring = useRef(!!focusHs);
  const pickReq = useRef(0);   // เลขคำขอล่าสุดของ pick — รายการสินค้าของพิกัดเก่าที่มาช้าห้ามทับของพิกัดใหม่
  const alive = useRef(true);  // หน้านี้ถูกถอดแล้ว (เปลี่ยนแท็บ/ย้อนกลับ) → งานที่ตอบช้าห้ามเขียน URL ของหน้าใหม่

  useEffect(() => {
    if (MOCK) return;
    db.searchHsRules('').then(setRules).catch(fail);
    db.listClassifications('').then(setClasses).catch(() => {});
    db.frequentHs().then(setFreq).catch(() => {});
    // รีเฟรชตอนเปิดพิกัดไว้ (URL /screening/<hs>) → เปิดพิกัดเดิมให้ · ไม่มีพิกัดนี้แล้ว = เอาออกจาก URL
    if (focusHs) db.getHsRule(focusHs)
      .then(r => {
        if (!restoring.current) return;
        if (r) { pick(r, true); return; }
        restoring.current = false; onPick?.(undefined); onMissing?.(focusHs);
      })
      .catch(() => {});
    return () => { alive.current = false; restoring.current = false; pickReq.current++; };
  }, []);

  const search = async (t) => {
    setTerm(t);
    try { setRules(await db.searchHsRules(t)); } catch (e) { fail(e); }
  };

  const pick = async (rule, fromRestore = false) => {
    if (!alive.current || (fromRestore && !restoring.current)) return;
    restoring.current = false;
    const my = ++pickReq.current;
    setPicked(rule);
    onPick?.(rule.hs_code);   // → URL (รีเฟรชแล้วได้พิกัดเดิม)
    try {
      const all = await db.listClassifications('');
      if (my === pickReq.current) setProducts(all.filter(p => p.hs_code === rule.hs_code));
    } catch { if (my === pickReq.current) setProducts([]); }
  };

  const pickHs = async (hsCode) => {
    const r = rules.find(x => x.hs_code === hsCode) ?? await db.getHsRule(hsCode);
    if (r) { setSuggestOpen(false); pick(r); }
  };

  const runSuggest = async (text) => {
    const lines = (text || suggestText).split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.length) { setSuggestions([]); return; }
    let all = rules;
    try { all = await db.searchHsRules(''); } catch { /* ใช้ rules ที่โหลดไว้ */ }
    setSuggestions(lines.map(line => ({
      line,
      prods: suggestFromProducts(line, classes),   // เคยนำเข้า (ยืนยันแล้ว) มาก่อน
      cands: suggestHs(line, all),                  // จับคำกับกฎพิกัด
    })));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    const text = await file.text();
    setSuggestText(text); setSuggestOpen(true);
    runSuggest(text);
  };

  if (MOCK) return (
    <div className="placeholder">
      <h2>คัดกรองสินค้าก่อนนำเข้า</h2>
      <p>โหมด MOCK — ต่อ Supabase เพื่อเช็ค verdict จริงจาก hs_rule (อากร/permit/DG/ความสด)</p>
    </div>
  );
  if (pending) return <DbPending onRetry={() => search('')}/>;

  const product = products[0] ?? null;
  const verdict = picked ? verdictOf(picked, product) : null;
  const permits = picked ? permitRows(picked) : [];
  const stale = picked ? staleRule(picked) : null;

  return (
    <div className="page">
      <div className="page-h">
        <h2>คัดกรองก่อนสั่ง</h2>
        <span className="meta">เลือกพิกัดที่จะนำเข้า → ดูว่านำเข้าได้ไหม เสียอากรเท่าไหร่ ต้องขออะไรก่อน</span>
      </div>

      <div className="scr">
        <div className="scr-list">
          <div className="suggest-bar">
            <button className={`sg-toggle ${suggestOpen ? 'on' : ''}`} onClick={() => setSuggestOpen(v => !v)}>
              ✧ ให้ระบบแนะนำพิกัดจากข้อมูลสินค้า
            </button>
          </div>
          {suggestOpen && (
            <div className="suggest-box">
              <div className="sg-hint">พิมพ์ชื่อ/รายละเอียดสินค้า (บรรทัดละ 1 อย่าง) หรืออัพโหลดไฟล์ .txt/.csv — ระบบเทียบกับ<b>สินค้าที่เคยนำเข้า</b>ก่อน แล้วค่อยจับคำกับกฎพิกัด</div>
              <textarea rows={3} value={suggestText} onChange={e => setSuggestText(e.target.value)}
                placeholder={'เช่น\nเต็นท์ 2 คน ผ้าโพลีเอสเตอร์\nขวดน้ำ Tritan\nเตาแก๊สพกพา'}/>
              <div className="sg-actions">
                <label className="btn2 filebtn">อัพโหลดไฟล์
                  <input type="file" accept=".txt,.csv" style={{ display: 'none' }} onChange={onFile}/>
                </label>
                <button className="btn1" onClick={() => runSuggest()}>แนะนำพิกัด</button>
              </div>
              {suggestions.map((s, i) => (
                <div className="sg-result" key={i}>
                  <div className="sg-line">{s.line}</div>
                  {s.prods.map(({ product }) => (
                    <button className="sg-cand used" key={'p' + product.id}
                      onClick={() => pickHs(product.hs_code)}>
                      <span className="sc-tag">เคยนำเข้า</span>
                      <span className="sc-code">{product.hs_code}</span>
                      <span className="sc-desc"><NameTh c={product}/></span>
                    </button>
                  ))}
                  {s.cands.map(({ rule }) => (
                    <button className="sg-cand" key={rule.hs_code}
                      onClick={() => { setSuggestOpen(false); pick(rule); }}>
                      <span className="sc-code">{rule.hs_code}</span>
                      <span className="sc-desc">{rule.description_th}</span>
                      <span className="sc-duty">{rule.duty_rate == null ? '—' : `${rule.duty_rate}%`}</span>
                    </button>
                  ))}
                  {!s.prods.length && !s.cands.length &&
                    <div className="sg-none">ยังไม่พบพิกัดที่ตรง — ค้นเองด้านล่าง หรือทำ dossier ให้ broker ตี</div>}
                </div>
              ))}
              <div className="sg-disc">คำแนะนำจากที่เคยนำเข้า/จับคำ — ต้องยืนยันกับ broker/Advance Ruling เสมอ</div>
            </div>
          )}
          {!suggestOpen && freq.length > 0 && (
            <div className="freqbox">
              <div className="freq-h">พิกัดที่ใช้บ่อย (broker ยืนยันแล้ว)</div>
              <div className="freq-chips">
                {freq.map(f => (
                  <button className="freq-chip" key={f.hs_code} title={f.sample}
                    onClick={() => pickHs(f.hs_code)}>
                    <b>{f.hs_code}</b> <span>{f.sample}</span> <i>×{f.count}</i>
                  </button>
                ))}
              </div>
            </div>
          )}
          <input className="search full" placeholder="ค้นพิกัด / ชื่อสินค้า…"
            value={term} onChange={e => search(e.target.value)}/>
          {error && <div className="ferr">{error}</div>}
          <div className="scr-rules">
            {rules.map(r => (
              <button key={r.hs_code} className={`scr-rule ${picked?.hs_code === r.hs_code ? 'on' : ''}`}
                onClick={() => pick(r)}>
                <span className="sr-code">{r.hs_code}</span>
                <span className="sr-desc">{r.description_th}</span>
                <span className="sr-duty">{r.duty_rate == null ? '—' : `${r.duty_rate}%`}</span>
              </button>
            ))}
            {!rules.length && <div className="jr-empty">ไม่พบพิกัด — ลองคำค้นอื่น หรือเพิ่มใน Settings</div>}
          </div>
        </div>

        <div className="scr-detail">
          {!picked ? (
            <div className="placeholder"><h2>เลือกพิกัดทางซ้าย</h2><p>เพื่อดูผลคัดกรอง</p></div>
          ) : (
            <>
              <div className="scr-verdict" style={{ '--vc': verdict.color }}>
                <span className="verlabel">[{verdict.label}]</span>
                <span className="verwhy">{verdict.reason}</span>
              </div>
              <div className="scr-hs">
                <div className="sh-code">{picked.hs_code}</div>
                <div className="sh-desc">{picked.description_th}</div>
              </div>

              <div className="scr-grid">
                <div className="sg-cell">
                  <div className="sg-label">อากร + VAT</div>
                  <div className="sg-val">
                    {picked.duty_rate == null ? 'อากรยังไม่ตรวจ' : `อากร ${picked.duty_rate}%`}
                    <span className="sg-sub"> + VAT {picked.vat_rate ?? 7}%</span>
                  </div>
                  {picked.fta_notes && <div className="sg-note">FTA: {picked.fta_notes}</div>}
                </div>

                <div className="sg-cell">
                  <div className="sg-label">ความสดของกฎ</div>
                  <div className="sg-val">
                    {stale.stale
                      ? <span className="freshbad">● ควร re-verify{stale.days != null ? ` (เก่า ${stale.days} วัน)` : ' (ยังไม่เคยตรวจ)'}</span>
                      : <span className="freshok">✓ ตรวจล่าสุด {picked.last_verified}</span>}
                  </div>
                  {picked.source_url && <div className="sg-note">ที่มา: {picked.source_url}</div>}
                </div>
              </div>

              <div className="scr-permits">
                <div className="sg-label">ใบอนุญาต / มาตรการ ({permits.length})</div>
                {permits.length ? permits.map((p, i) => (
                  <div className={`permit-card k-${p.cat}`} key={i}>
                    <div className="pc-top">
                      <span className={`sbadge ${p.side === 'export' ? 'exp' : 'imp'}`}>{p.sideLabel}</span>
                      <span className="pc-name">{p.label}</span>
                      {p.agency && <span className="pc-agency">{p.agency}</span>}
                      <span className="pc-when">{whenLabel(p.neededBy)}</span>
                    </div>
                    {p.note && <div className="pc-note">{p.note}</div>}
                    {p.remindBefore && <div className="pc-remind">เริ่มขอล่วงหน้า: {whenLabel(p.remindBefore)} (ออกย้อนหลังไม่ได้)</div>}
                  </div>
                )) : <div className="permit-none">ยังไม่พบมาตรการของพิกัดนี้ในระบบ — แปลว่ายังไม่มีข้อมูล ไม่ได้ยืนยันว่าไม่ต้องขอ</div>}
              </div>

              {picked.notes && <div className="scr-extra"><b>หมายเหตุ:</b> {picked.notes}</div>}

              {product && product.hs_confirmed === false && (
                <div className="advance-ruling">
                  HS ยังไม่ยืนยัน → แนะนำขอ <b>Advance Ruling</b> จากกรมศุลกากร (ยื่นเป็นลายลักษณ์อักษรก่อนนำเข้า
                  มีผลผูกพัน) เพื่อล็อกพิกัดก่อนสั่ง
                </div>
              )}

              <div className="disclaimer">
                ⓘ ข้อมูลนี้ช่วยคัดกรองเบื้องต้น — พิกัด/เงื่อนไขจริงยึดตามกรมศุลกากร / broker / Advance Ruling ·
                อ้างอิงกฎ ณ {picked.last_verified ?? 'ยังไม่ตรวจ'} · ที่มา {picked.source_url ?? '—'}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const whenLabel = (nb) => ({
  before_pay: 'ก่อนจ่ายเงิน', before_ship: 'ก่อนของออก', before_pickup: 'ก่อนรับของ',
  before_clear: 'ก่อนเดินพิธี', on_arrival: 'ตอนของถึง',
}[nb] ?? nb);
